import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import { CircleAlert, CircleHelp, TriangleAlert } from "lucide-react";

import { BADGE_TONES } from "../theme/badgeTones";
import { Button } from "./Button";
import { Dialog, DialogContent } from "./Dialog";

// Same icons and colours as AlertNote; only `danger` turns the confirm button red.
const TONES = {
  info: { Icon: CircleHelp, color: BADGE_TONES.info.color, buttonVariant: "default" },
  warning: { Icon: CircleAlert, color: BADGE_TONES.pending.color, buttonVariant: "default" },
  danger: { Icon: TriangleAlert, color: "error.main", buttonVariant: "destructive" },
};

/**
 * Confirmation dialog: tone icon + title, optional question, explanation, extra content and Cancelar / confirm.
 * While `loading` it can't be closed and the confirm button shows `loadingLabel`.
 */
export const ConfirmModal = ({
  open,
  onClose,
  onConfirm,
  title,
  question,
  description,
  children,
  confirmLabel,
  loadingLabel,
  loading = false,
  confirmDisabled = false,
  tone = "info",
  maxWidth = "xs",
}) => {
  const { Icon, color, buttonVariant } = TONES[tone];
  const close = () => {
    if (!loading) onClose();
  };

  return (
    <Dialog open={open} onOpenChange={close} maxWidth={maxWidth} fullWidth sx={{ zIndex: 100000 }}>
      <DialogContent sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2.5 }}>
        <Box sx={{ display: "flex", gap: 1.5 }}>
          <Box sx={{ color, display: "flex", pt: "2px", flexShrink: 0 }}>
            <Icon size={22} />
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 0, flex: 1 }}>
            <Typography variant="h6" component="h2" sx={{ fontSize: "1.125rem", fontWeight: 600, lineHeight: 1.4 }}>
              {title}
            </Typography>
            {question && (
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {question}
              </Typography>
            )}
            {description && (
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {description}
              </Typography>
            )}
            {children}
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: 1.5, justifyContent: "flex-end" }}>
          <Button variant="outline" onClick={close} disabled={loading}>
            Cancelar
          </Button>
          <Button variant={buttonVariant} onClick={onConfirm} disabled={loading || confirmDisabled}>
            {loading && loadingLabel ? loadingLabel : confirmLabel}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

ConfirmModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  title: PropTypes.node.isRequired,
  question: PropTypes.node,
  description: PropTypes.node,
  children: PropTypes.node,
  confirmLabel: PropTypes.node.isRequired,
  loadingLabel: PropTypes.node,
  loading: PropTypes.bool,
  confirmDisabled: PropTypes.bool,
  tone: PropTypes.oneOf(["info", "warning", "danger"]),
  maxWidth: PropTypes.string,
};
